/**
 * ContextMenuItem — описание одного пункта доменного контекстного меню.
 *
 * Домен отдаёт массив таких пунктов через геттер сервиса (например
 * AssembliesService.contextMenu), а грид рендерит их и в кебаб-колонке,
 * и в меню по правому клику.
 *
 * Все колбэки получают строку (row), потому что доступность/видимость
 * пункта обычно зависит от конкретной записи.
 */
class ContextMenuItem<TRow = any> {
  /** Подпись пункта */
  public title: ((row: TRow) => string) | string;

  /** Material-иконка слева */
  public icon: string;

  /**
   * Действие по клику. Получает строку.
   * Может вернуть промис — грид дождётся его перед refresh. Тип результата
   * не важен (Promise<unknown>): грид игнорирует значение, ему нужен лишь
   * факт завершения действия.
   */
  public callback: (row: TRow) => void | Promise<unknown>;

  /**
   * Причина недоступности пункта.
   * - строка        — пункт всегда задисейблен, строка идёт в title (тултип)
   * - функция       — вычисляется от строки: вернула строку → задисейблен с этим текстом, null → доступен
   * - null / не задан — доступен
   */
  public disabled: ((row: TRow) => string | null) | string | null;

  /**
   * Скрыть пункт целиком (аналог v-if).
   * - boolean  — статично
   * - функция  — вычисляется от строки
   * - не задан — пункт виден
   */
  public hidden: ((row: TRow) => boolean) | boolean;

  /**
   * Обновлять ли грид после выполнения callback.
   * По умолчанию true. Ставить false для действий, не меняющих список
   * (например «История действий» — открывает диалог, строки не трогает).
   */
  public refreshAfter: boolean;

  /** Нарисовать разделитель <hr> после этого пункта */
  public dividerAfter: boolean;

  /**
   * Идентификатор пункта. Нужен слушателю contextMenuItem:done, когда тот
   * реагирует не на любое действие, а на конкретное: сравнивать по title
   * нельзя — это подпись из UI, её меняют не задумываясь.
   */
  public id: string | undefined;

  /** Закрыть диалог (resolve), если callback вернул true. Обрабатывает VDialogV3 */
  public closeDialogOnSuccess: boolean;

  constructor(
    title:                ((row: TRow) => string) | string,
    icon:                 string,
    callback:             (row: TRow) => void | Promise<unknown>,
    disabled:             ((row: TRow) => string | null) | string | null = null,
    hidden:               ((row: TRow) => boolean) | boolean              = false,
    refreshAfter:         boolean                                         = true,
    dividerAfter:         boolean                                         = false,
    id:                   string | undefined                              = undefined,
    closeDialogOnSuccess: boolean                                         = false,
  ) {
    this.title                = title;
    this.icon                 = icon;
    this.callback             = callback;
    this.disabled             = disabled;
    this.hidden               = hidden;
    this.refreshAfter         = refreshAfter;
    this.dividerAfter         = dividerAfter;
    this.id                   = id;
    this.closeDialogOnSuccess = closeDialogOnSuccess;
  }
}

export default ContextMenuItem;